import {ActivityStatus, CWDetection} from '@transparent/coretypes';
import {LessonVO} from '../vo/lesson-vo';
import {ActivityVO} from '../vo/activity-vo';

export interface CurrentIndex {
    li: number;
    ai: number;
    isAssessment?: boolean;
}

/**
 * Assessment minimum score
 */
export const MIN_SCORE = 80;

/**
 * Checks if last char of the value equals to  '/' and appends
 * it if it is not.
 */
export function normalizePath(value: string): string {
    return (value[value.length - 1] !== '/') ? value + '/' : value;
}

export function normalizeCaretPos(): void {
    if (CWDetection.isIOS) {
        const sel = window.getSelection();
        if (sel && sel.rangeCount) {
            const range = sel.getRangeAt(0);
            range.setStart(sel.focusNode, range.endOffset);
            range.setEnd(sel.focusNode, range.endOffset);
            sel.removeAllRanges();
            sel.addRange(range);
        }
    }
}

/**
 * Calculates amount of completed activities
 * {Array.<LessonVo>} lessons List of lessons
 * {number} amount of completed activities in the unit
 */
export function calculateTotalCompleted(lessons: Array<LessonVO> = [], includeAssessment = true): number {
    let totalCompleted = 0;
    lessons = lessons || [];
    for (const lesson of lessons) {
        if (lesson.isAssessment) {
            totalCompleted += includeAssessment && (lesson.status === ActivityStatus.COMPLETED
            || lesson.status === ActivityStatus.PASSED) ? 1 : 0;
        } else {
            totalCompleted += calculateLessonCompletion(lesson.activities);
        }
    }
    return totalCompleted;
}

/**
 * Calculates amount of activities
 * lessons Vector of LessonVos
 * {Number} amount of activities in the unit
 */
export function calculateTotalActivities(lessons: Array<LessonVO> = [], includeAssessment = true): number {
    lessons = lessons || [];
    let totalActivities = 0;
    for (const lesson of lessons) {
        if (lesson && lesson.isAssessment) {
            totalActivities += includeAssessment ? 1 : 0;
        } else if (lesson && lesson.activities) {
            totalActivities += lesson.activities.length;
        }
    }
    return totalActivities;
}

/**
 * Indicates that all lessons are completed
 * lessons List (array) of LessonVo's
 * {boolean}
 */
export function isAllLessonsCompleted(lessons: Array<LessonVO> = []): boolean {
    if (lessons) {
        let len = lessons.length;
        len = lessons[len - 1].isAssessment ? len - 1 : len;
        let completeCount = 0;
        for (let i = 0; i < len; i++) {
            if (isAllActivitiesCompleted(lessons[i].activities)) {
                completeCount++;
            }
        }
        return completeCount === len;
    }
    return false;
}

/**
 * Indicates that all activities are completed
 * activities Vector of ActivityVo's
 * {boolean}
 */
export function isAllActivitiesCompleted(activities: ActivityVO[]): boolean {
    if (hasRequiredActivity(activities)) {
        return isRequiredComplete(activities);
    } else {
        let count = 0;
        const len = activities.length;
        for (const act of activities) {
            if (act.status === 'completed') {
                count++;
            }
        }
        return count === len;
    }
}

/**
 *
 * {ActivityVO} activity
 * {boolean}
 */
export function isLearnlet(activity: ActivityVO): boolean {
    return activity && activity.moduleName === 'AlphabetLearnlet';
}

/**
 *
 * {ActivityVO} activity
 * {boolean}
 */
export function isMln(activity: ActivityVO): boolean {
    return activity && /MLN/i.test(activity.moduleName);
}

/**
 *
 * activities Alist containing  ActivityVo value objects
 * {Number} Amount of completed activities
 */
export function calculateLessonCompletion(activities: ActivityVO[] = []): number {
    let completedCount = 0;
    for (const act of activities) {
        completedCount = act.status === ActivityStatus.COMPLETED
        || act.status === ActivityStatus.PASSED ? completedCount + 1 : completedCount;
    }
    return completedCount;
}

/**
 *
 * lessons
 * {*}
 */
export function getIsAssessmentEnabled(lessons: LessonVO[] = []): boolean {
    lessons = lessons || [];
    for (const lesson of lessons) {
        if (hasRequiredActivity(lesson.activities)) {
            return isRequiredComplete(lesson.activities);
        }
    }
    return false;
}

/**
 * Sets lessons completion percentage
 * {Array} lessons List (array) of lessons (LessonVO)
 */
export function setLessonsCompletionPercentage(lessons: LessonVO[] = []): void {
    lessons = lessons || [];
    for (const lesson of lessons) {
        const activities = lesson.activities;
        const a = activities.length;
        const b = calculateLessonCompletion(activities);
        lesson.percentage = Math.min(100, Math.round(((b / a) || 0) * 100));
    }
}

/**
 * Calculates percent completion of unit
 * {Array} lessons List (array) of lessons (LessonVO)
 * {number} Percent completion
 */
export function getUnitCompletionPercentage(lessons: LessonVO[] = [], includeAssessment = false): number {
    const total = calculateTotalActivities(lessons, includeAssessment);
    const numCompleted = calculateTotalCompleted(lessons, includeAssessment);
    if (total) {
        return calculateCompletionStatus(numCompleted, total);
    }
    return 0;
}

/**
 *
 * activities
 * {boolean}
 */
export function hasRequiredActivity(activities: ActivityVO[] = []): boolean {
    for (const act of activities) {
        if (act.required === true) {
            return true;
        }
    }
    return false;
}

/**
 * Gets number of required activities from given array of activityVo's
 * activities
 * {number}
 */
export function getNumRequiredActivities(activities: ActivityVO[] = []): number {
    let count = 0;
    for (const act of activities) {
        if (act.required === true) {
            count++;
        }
    }
    return count;
}

/**
 *
 * lessons
 * lessonIndex
 * actIndex
 * {Object}
 */
export function getSingleLessonIncompleteActivityIndex(lessons: LessonVO[],
                                                       lessonIndex: number,
                                                       actIndex: number): CurrentIndex {
    const lesson = lessons[lessonIndex];
    let acts, next;
    acts = lesson.activities;
    if (actIndex === -1) {
        actIndex = 0;
    }
    if (actIndex > 0) {
        next = getFirstIncompleteActivityIndex(acts.slice(actIndex + 1));
        if (next !== -1) {
            next += 1;
            return {li: lessonIndex, ai: actIndex + next};
        }
    }
    next = getFirstIncompleteActivityIndex(acts);
    if (next >= 0) {
        return {li: lessonIndex, ai: next};
    }
    return null;
}

/**
 *
 * lessons
 * lessonIndex
 * actIndex
 * isLessonMode
 * {Number|Object}
 */
export function getUnitNextIncompleteActivityIndex(lessons: LessonVO[],
                                                   lessonIndex: number,
                                                   actIndex: number,
                                                   isLessonMode?: boolean): CurrentIndex {
    if (lessonIndex === -1) {
        lessonIndex = 0;
    }
    if (actIndex === -1) {
        actIndex = 0;
    }
    if (lessonIndex >= lessons.length) {
        lessonIndex = lessons.length - 1;
    }
    if (isLessonMode) {
        return getSingleLessonIncompleteActivityIndex(lessons, lessonIndex, actIndex);
    }
    let hasAssessment = false;
    lessons = lessons || [];
    let len = lessons.length;
    if (len === 0) {
        return null;
    }
    len = len - 1;
    if (lessons[len].isAssessment) {
        len--;
        hasAssessment = true;
    }
    let lesson = lessons[lessonIndex], i;
    let next;
    if (actIndex > 0) {
        next = getFirstIncompleteActivityIndex(lesson.activities.slice(actIndex + 1));
        if (next !== -1) {
            next += 1;
        }
    } else {
        next = getFirstIncompleteActivityIndex(lesson.activities);
    }
    if (actIndex === -1) {
        actIndex = 0;
    }
    if (next >= 0) {
        return {li: lessonIndex, ai: actIndex + next};
    }
    for (i = lessonIndex + 1; i <= len; i++) {
        lesson = lessons[i];
        next = getFirstIncompleteActivityIndex(lesson.activities);
        if (next >= 0) {
            return {li: i, ai: next};
        }
    }

    for (i = 0; i <= lessonIndex; i++) {
        lesson = lessons[i];
        next = getFirstIncompleteActivityIndex(lesson.activities);
        if (next >= 0) {
            return {li: i, ai: next};
        }
    }
    if (hasAssessment) {
        return {li: len + 1, ai: 0, isAssessment: true};
    }
    return null;
}

/**
 *
 * activities
 * {boolean}
 */
export function isRequiredComplete(activities: ActivityVO[] = []): boolean {
    const arr = activities.filter((act: ActivityVO) => act.required && act.status === 'completed');
    return arr.length === getNumRequiredActivities(activities);
}

/**
 * {number}
 */
export function getFirstIncompleteActivityIndex(activities: ActivityVO[] = []): number {
    const len = activities.length;
    for (let i = 0; i < len; i++) {
        if (activities[i].status !== 'completed') {
            return i;
        }
    }
    return -1;
}

/**
 * Calculate percentage of given correct answers
 * {number} numCorrect Count of correct answers
 * {number} total Total count of questions
 * {number} Completion percentage
 */
export function calculateCompletionStatus(numCorrect: number, total: number): number {
    if (isNaN(numCorrect) || isNaN(total)) {
        return 0;
    }
    if (numCorrect === 0 && total === 0) {
        return 100;
    }
    return Math.min(100, Math.round(((numCorrect / total) || 0) * 100));
}
